import { useEffect, useState } from "react";

/**
 * «Llegó SOA 10 PRO» — hoja inferior de versión nueva (brand book §13, pantalla 4).
 *
 * Aparece cuando el service worker detecta una versión nueva (main.jsx despacha
 * "sw-update-available"). «Actualizar ahora» activa el SW nuevo, borra el precache y recarga:
 * es lo que saca a quien tiene guardada la app vieja (el SW viejo precacheaba el index.html).
 * No pierde nada guardado: solo caches del navegador.
 */
export function SwUpdateBanner() {
  const [visible, setVisible] = useState(false);
  const [trabajando, setTrabajando] = useState(false);
  const [lento, setLento] = useState(false);

  useEffect(() => {
    const handler = () => setVisible(true);
    window.addEventListener("sw-update-available", handler);
    return () => window.removeEventListener("sw-update-available", handler);
  }, []);

  if (!visible) return null;

  // Limpia lo guardado y recarga con una dirección distinta (así el navegador pide la página nueva a la red y no usa nada viejo).
  // Todo con tope de tiempo: antes, si la limpieza se colgaba, el botón se quedaba en «Actualizando…» sin salida.
  const conTope = (p, ms) => Promise.race([p, new Promise((r) => setTimeout(r, ms))]);
  const actualizar = async () => {
    setTrabajando(true); setLento(false);
    setTimeout(() => setLento(true), 6000);
    try {
      await conTope((async () => {
        const regs = await navigator.serviceWorker?.getRegistrations?.();
        await Promise.all((regs || []).map(r => r.unregister().catch(() => {})));
        const keys = await caches?.keys?.();
        await Promise.all((keys || []).map(k => caches.delete(k).catch(() => {})));
      })(), 3000);
    } catch { /* si falla la limpieza, igual se recarga */ }
    try { const u = new URL(window.location.href); u.searchParams.set("_v", String(Date.now())); window.location.replace(u.toString()); }
    catch { window.location.reload(); }
  };

  return (
    <div role="dialog" aria-live="polite" aria-label="Nueva versión disponible"
         className="fixed inset-x-0 bottom-0 z-[9999] flex justify-center px-3 pb-[100px] md:pb-5 pointer-events-none">
      <div className="pointer-events-auto w-full max-w-[440px] bg-card text-foreground rounded-[24px] border border-border p-5 shadow-[0_8px_32px_rgba(17,19,24,0.18)]"
           style={{ animation: "swBannerIn 0.3s ease-out" }}>
        <div className="flex items-start gap-3">
          <span aria-hidden="true" className="grid place-items-center w-11 h-11 rounded-2xl bg-accent/10 text-accent shrink-0">
            <span className="msr" style={{ fontSize: 24 }}>refresh</span>
          </span>
          <div className="min-w-0">
            <div className="text-[18px] font-extrabold tracking-tight">Llegó SOA 10 PRO</div>
            <p className="mt-1 text-[14px] leading-snug text-muted-foreground">Tu navegador tiene guardada la versión anterior. Actualizá para entrar.</p>
          </div>
        </div>
        <button onClick={actualizar} disabled={trabajando && !lento}
                className="mt-4 w-full h-12 rounded-[15px] bg-foreground text-background text-[15px] font-extrabold hover:opacity-90 transition-opacity disabled:opacity-60">
          {lento ? "Reintentar" : trabajando ? "Actualizando…" : "Actualizar ahora"}
        </button>
        <div className="mt-2 text-center text-[12px] text-muted-foreground">{lento ? "Está tardando más de lo normal. Probá «Reintentar» o cerrá la app del todo y volvé a abrirla." : "Tarda unos segundos · no perdés nada guardado"}</div>
      </div>
    </div>
  );
}
